import type { AgentSource } from '@/types/chat'
import { isFinished, type ActivityItem, type ActivityKind } from './activity-thread-types'

/** One icon in the finished card's header stack: a kind of step the run took, red when any step of that kind failed. */
export interface ActivityStackIcon {
  kind: ActivityKind
  failed: boolean
}

export interface ActivitySummary {
  steps: number
  failed: number
  stack: ActivityStackIcon[]
}

export function summarizeActivity(items: readonly ActivityItem[]): ActivitySummary {
  const stack: ActivityStackIcon[] = []
  let failed = 0
  for (const item of items) {
    const itemFailed = item.status === 'failed'
    if (itemFailed) failed += 1
    const known = stack.find((icon) => icon.kind === item.kind)
    if (known) known.failed ||= itemFailed
    else stack.push({ kind: item.kind, failed: itemFailed })
  }
  return { steps: items.length, failed, stack }
}

export function runningStep(items: readonly ActivityItem[]): ActivityItem | undefined {
  return items.find((item) => item.status === 'active')
}

/** Whether a step's status change is the moment it ends, as opposed to arriving already ended. */
export function endsNow(
  previous: ActivityItem['status'] | undefined,
  next: ActivityItem['status'],
): boolean {
  return previous !== undefined && !isFinished(previous) && isFinished(next)
}

/** Every web page the run read, each named once, in the order the run read them. */
export function webSourcesOf(items: readonly ActivityItem[]): AgentSource[] {
  const pages = items
    .flatMap((item) => item.sources ?? [])
    .filter((source) => source.kind === 'web')
  return [...new Map(pages.map((source) => [source.ref, source])).values()]
}
